import React, { useCallback, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import { getPaymentOption } from 'src/public/components/default_template/online_ordering/checkout/checkoutUtils'
import { getReadyTimeChangeBucket } from 'src/public/components/default_template/online_ordering/checkout/fulfillmentTimeUtils'
import { usePayment } from 'src/public/components/online_ordering/PaymentContext'
import { t } from 'src/translations'

import Link from 'shared/components/common/link'
import ContextualLoadingSpinner from 'shared/components/common/loading_spinner/LoadingSpinner'
import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  useModal
} from 'shared/components/common/modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { toLocalTimeWithTimezone } from 'shared/js/timeUtils'

import { useCart } from 'public/components/online_ordering/CartContext'
import { useCheckout } from 'public/components/online_ordering/CheckoutContext'
import { OrderError } from 'public/components/online_ordering/CheckoutContext'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import { getQuoteTime } from 'public/components/online_ordering/fulfillmentUtils'
import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'

const CheckoutError = ({ error }: { error?: OrderError | null }) => {
  const { cart } = useCart()
  const tracker = useTracker()

  if (!error || !cart) {
    return null
  }

  switch (error.type) {
    case 'OUT_OF_STOCK':
    case 'oos-item-removed':
      return <>{t('checkout-error-modal.out-of-stock')}</>
    case 'ORDER_TIME_UNAVAILABLE':
      return <>{t('checkout-error-modal.order-time-unavailable')}</>
    case 'UNSUPPORTED_PROMO_CODE':
      return (
        <>
          <div>{t('checkout-error-modal.promo-code-redeemed')}</div>
          <div>{t('checkout-error-modal.temporary-hold-notice')}</div>
        </>
      )
    case 'INVALID_CREDIT_CARD':
      return <>{t('checkout-error-modal.credit-card-declined')}</>
    case 'OO_DISABLED':
    case 'online-ordering-disabled':
      return <>{t('checkout-error-modal.online-ordering-disabled')}</>
    case 'PLACE_ORDER_EMPTY_CART':
    case 'CART_CONTENT_NOT_FOUND':
    case 'empty-cart-place-order':
    case 'cart-content-not-found':
      return <>{t('checkout-error-modal.add-items-to-order')}</>
    case 'PLACE_ORDER_INVALID_EMAIL':
      return <>{t('checkout-error-modal.enter-valid-email')}</>
    case 'UNSUPPORTED_PAYMENT_METHOD':
      return <>{t('checkout-error-modal.unsupported-payment-method')}</>
    case 'SEMI_INTEGRATED_AMOUNT_MISMATCH':
      return <>{t('checkout-error-modal.payment-amount-mismatch')}</>
    case 'MISSING_CUSTOMER_INFO':
      return <>{t('checkout-error-modal.enter-customer-info')}</>
    case 'MISSING_DELIVERY_INFO':
      return <>{t('checkout-error-modal.enter-delivery-address')}</>
    case 'DINING_OPTION_UNAVAILABLE':
      return (
        <>
          {t('checkout-error-modal.dining-option-unavailable', {
            diningOption:
              cart.diningOptionBehavior === DiningOptionBehavior.Delivery
                ? t('checkout-error-modal.delivery')
                : t('checkout-error-modal.take-out')
          })}
        </>
      )
    case 'NOT_IN_DELIVERY_AREA':
      return <>{t('checkout-error-modal.not-in-delivery-area')}</>
    case 'ALCOHOL_UNSUPPORTED':
    case 'place-order:alcohol-unsupported':
      return <>{t('checkout-error-modal.alcohol-unsupported')}</>
    case 'AUTOFIRE_OFFLINE':
      return <>{t('checkout-error-modal.autofire-offline')}</>
    case 'CRITICAL_ERROR':
    case 'PLACE_ORDER_FAILED':
    case 'critical-error':
    case 'cart-operation-failed':
    case 'cart-operation-invalid':
      return (
        <>
          <span>{t('checkout-error-modal.order-placement-failed')}</span>
          {error.paymentWasConfirmed && (
            <span>{t('checkout-error-modal.temporary-hold-notice')}</span>
          )}
        </>
      )
    case 'CUSTOM_ERROR_MESSAGE':
      return <>{error.message}</>
    case 'generic-graphql-error':
      return <>{t('checkout-error-modal.order-could-not-be-sent')}</>
    case 'DISCOUNT_RELATED_ERROR':
      return <>{t('checkout-error-modal.promo-code-cannot-be-applied')}</>
    default: {
      // Some error messages come through as non-customer-facing, stringified error objects formatted as "[<ErrorType>(reasonCode=<string>, reasonMsg=<string>)]"
      // In that case, pull out the contents of the reasonMsg field.  Otherwise, display the original message.
      const reasonMsg =
        error.message.split('reasonMsg=')[1]?.slice(0, -2) ?? error.message
      tracker.track(TrackingEvents.UNKNOWN_CHECKOUT_ERROR_TYPE, {
        cartSource: cart.cartSource,
        type: error.type,
        message: error.message
      })
      return <>{reasonMsg}</>
    }
  }
}

const CheckoutErrorModal = ({
  onSubmit
}: {
  onSubmit: () => Promise<void>
}) => {
  const { isOpen, onClose, onOpen } = useModal()
  const {
    orderError,
    setOrderError,
    giftCardAppliedAmount,
    orderTotal,
    saveNewAddress
  } = useCheckout()
  const { refetchCart, cart, cartGuid, loadingCart } = useCart()
  const tracker = useTracker()
  const { paymentOption, paymentType } = usePayment()
  const isIntlRestaurant = useIsIntlRestaurant()
  const error = useRef<OrderError | null>(null)
  const { savedAddressUsed } = useDelivery()
  const { fulfillmentData } = useFulfillment()

  const trackError = useCallback(() => {
    if (cart) {
      tracker.track(TrackingEvents.ORDER_PLACEMENT_FAILED, {
        cartGuid: cartGuid,
        cartSource: cart.cartSource,
        readyTimeChangeBucket: getReadyTimeChangeBucket(cart, cartGuid, false),
        errorMsg: orderError?.message,
        errorType: orderError?.type,
        paymentType: getPaymentOption(
          paymentType,
          paymentOption,
          giftCardAppliedAmount,
          orderTotal,
          isIntlRestaurant
        ),
        diningOption: cart?.diningOptionBehavior,
        deliveryProvider: cart?.deliveryProviderInfo?.provider,
        saveNewAddress,
        savedAddressUsed:
          cart?.diningOptionBehavior === DiningOptionBehavior.Delivery
            ? savedAddressUsed
            : undefined,
        validDeliveryAddressPresent:
          !!fulfillmentData?.cartFulfillmentData?.deliveryInfo
      })
    }
  }, [
    cart,
    tracker,
    cartGuid,
    orderError?.message,
    orderError?.type,
    paymentType,
    paymentOption,
    giftCardAppliedAmount,
    orderTotal,
    isIntlRestaurant,
    saveNewAddress,
    savedAddressUsed,
    fulfillmentData?.cartFulfillmentData?.deliveryInfo
  ])

  useEffect(() => {
    if (orderError && !loadingCart) {
      // This check ensures we do not track the same error multiple times
      if (
        error.current === null ||
        error.current.type !== orderError.type ||
        orderError.message !== error.current.message
      ) {
        error.current = orderError
        onOpen()
        trackError()
      }
    }
  }, [orderError, onOpen, trackError, cart, loadingCart])

  const closeModal = useCallback(() => {
    setOrderError(null)
    error.current = null
    onClose()
    if (orderError?.refetchCart) {
      refetchCart()
    }
  }, [orderError, refetchCart, setOrderError, onClose])

  const orderTimeChangedErrors = [
    'place-order:order-time-changed',
    'order-time',
    'ORDER_TIME_CHANGED'
  ]

  return (
    <>
      <Modal onClose={closeModal} isOpen={isOpen} preventOverlayClose>
        <ModalOverlay />
        <ModalContent>
          {orderError?.type &&
          orderTimeChangedErrors.includes(orderError.type) ? (
            <OrderTimeChangedErrorModalContent onSubmit={onSubmit} />
          ) : (
            <div className='checkoutErrorModalContent'>
              <div className='header'>
                <h3>{t('checkout-error-modal.error-placing-order')}</h3>
                <ModalCloseButton />
              </div>
              {orderError && (
                <div className='body'>
                  <CheckoutError error={orderError} />
                </div>
              )}
            </div>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}

const OrderTimeChangedErrorModalContent = ({
  onSubmit
}: {
  onSubmit: () => Promise<void>
}) => {
  const { cart, refetchCart } = useCart()
  const { ooRestaurant } = useRestaurant()
  const { orderPath } = useRestaurantRoutes()
  const {
    formState: { isSubmitting }
  } = useFormContext()

  useEffect(() => {
    refetchCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!cart || !ooRestaurant) {
    return null
  }

  const newOrderTime =
    cart.fulfillmentType === 'ASAP'
      ? t('checkout-error-modal.order-time', {
          orderTime: getQuoteTime(cart.diningOptionBehavior, ooRestaurant, cart)
        })
      : toLocalTimeWithTimezone(
          cart.fulfillmentDateTime || '',
          ooRestaurant.timeZoneId ?? undefined
        )

  const diningBehavior =
    cart.diningOptionBehavior === DiningOptionBehavior.TakeOut
      ? t('checkout-error-modal.pickup')
      : t('checkout-error-modal.delivery')

  return (
    <div className='checkoutErrorModalContent'>
      <div className='header'>
        <h3>{t('checkout-error-modal.order-delayed-proceed')}</h3>
        <ModalCloseButton />
      </div>
      <div className='body'>
        <div>
          {t('checkout-error-modal.selected-time-unavailable', {
            diningBehavior,
            newOrderTime
          })}
        </div>
        <div className='buttonContainer'>
          <button
            className='primaryCta proceed'
            type='submit'
            onClick={onSubmit}
          >
            {isSubmitting ? (
              <ContextualLoadingSpinner size='24px' strokeWidth='3px' />
            ) : (
              t('checkout-error-modal.place-order')
            )}
          </button>
          <Link className='primaryCta cancel' href={orderPath}>
            {t('checkout-error-modal.cancel-order')}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default CheckoutErrorModal
